<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <BaseSelectDropdown
        :items="dropdownTypes"
        :selected-item="selectedQuestionTypeItem"
        :label="$t('questionnaire.questionnaireType')"
        show-error-as-tooltip
        clearable
        :error="$v.questionsType.required.$invalid ? $v.questionsType.required.$message : ''"
        @update:selected="handleQuestionTypeSelection"
        name="alterCodeType" />
      <div>
        <label for="name" class="text-sm font-medium">{{
          $t('questionnaire.traderSiteGroup')
        }}</label>
        <BaseSearchDropdown
          v-model="selectedItem"
          :list-items="dropdownTraderSiteGroups"
          :count="traderSiteGroupCount"
          :noResultsText="$t('messages.noResultsText')"
          show-error-as-tooltip
          :error="
            $v.traderGroupItemID.required.$invalid ? $v.traderGroupItemID.required.$message : ''
          "
          @clear-selection="handleTraderSiteGroupSelection"
          @updateSearch="updateTraderSiteGroupSearch"
          @item-selected="handleTraderSiteGroupSelection"></BaseSearchDropdown>
      </div>

      <BaseToggle
        :label="$t('baseObject.isActive')"
        name="isActive"
        v-model="modifiedData.isActive" />
      <BaseToggle
        :label="$t('baseObject.isFromManager')"
        name="isFromManager"
        v-model="modifiedData.isFromManager" />
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { enumToDropdownItems } from '@/utils/enums';

import BaseToggle from '../common/BaseToggle.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseSearchDropdown from '../common/BaseSearchDropdown.vue';

import { eQuestionsType, type Assortment } from '@/types/Materials';
import { type SelectDropdownItem } from '@/types/Base';
import { deepCopy } from '@/utils/data';
import { getGroupItemsWithTradeSites } from '@/services/api/groups';

const { t: $t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['closeModal', 'save']);

const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    assortment: Assortment;
    traderGroupItemNames: SelectDropdownItem[];
  }>(),
  {}
);

const modifiedData = ref<Assortment>(deepCopy<Assortment>(props.assortment));
const isModified = ref<boolean>(false);

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.questionsType = newData.bindingType || '';
    validationModel.value.traderGroupItemID = newData.traderGroupItemID || '';

    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.assortment);
  },
  { deep: true }
);

const dropdownTypes = ref(enumToDropdownItems(eQuestionsType));

const selectedItem = ref<SelectDropdownItem | null>(null);

const dropdownTraderSiteGroups = ref<SelectDropdownItem[]>(props.traderGroupItemNames);

const traderSiteGroupCount = ref(props.traderGroupItemNames.length || 0);

function handleTraderSiteGroupSelection(item: SelectDropdownItem) {
  selectedItem.value = item;
  if (modifiedData.value && item) {
    modifiedData.value.traderGroupItemNamePath = item.name;
    modifiedData.value.traderGroupItemID = item.value as string;
  }
}

async function updateTraderSiteGroupSearch(searchInput: string, page?: number) {
  const items = await getGroupItemsWithTradeSites({
    page: page,
    itemsPerPage: 20,
    query: searchInput,
    sortColumn: 'name',
    sortAscending: true,
  });
  if (items.count > 0 && items.data) {
    if (page && page > 1 && dropdownTraderSiteGroups.value)
      dropdownTraderSiteGroups.value = dropdownTraderSiteGroups.value.concat(
        items.data.map((t) => ({
          value: t.id,
          name: t.name,
        })) as SelectDropdownItem[]
      );
    else
      dropdownTraderSiteGroups.value = items.data.map((t) => ({
        value: t.id || '',
        name: t.name,
      }));
  } else dropdownTraderSiteGroups.value = [];
  traderSiteGroupCount.value = items.count;
}

const selectedQuestionTypeItem = ref<SelectDropdownItem | null>(null);
function handleQuestionTypeSelection(item: SelectDropdownItem) {
  selectedQuestionTypeItem.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.bindingType = item ? +item.value : null;
    modifiedData.value.questionsType = item ? item.name : undefined;
  }
}

function onDismissChanges() {
  modifiedData.value = deepCopy<Assortment>(props.assortment);
  emit('closeModal');
}

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  emit('save', modifiedData.value);
}

onMounted(async () => {
  if (modifiedData.value && modifiedData.value.bindingType !== undefined) {
    const selectedValue = dropdownTypes.value.find(
      (item) => item.value === modifiedData.value?.bindingType
    );

    if (selectedValue) {
      selectedQuestionTypeItem.value = selectedValue;
    }
  }
  if (modifiedData.value && modifiedData.value.traderGroupItemID !== undefined) {
    const selectedValue = props.traderGroupItemNames.find(
      (item) => item.value === modifiedData.value?.traderGroupItemID
    );

    if (selectedValue) {
      selectedItem.value = selectedValue;
    }
  }
});

const validationModel = ref({
  questionsType: modifiedData.value.bindingType || '',
  traderGroupItemID: modifiedData.value.traderGroupItemID || '',
});

const rules = computed(() => ({
  questionsType: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  traderGroupItemID: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
